import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { userDetailsStore } from 'stores/userDetailsStore/userDetailsStore'
import type { TUnitConverterOptionsWithUnits, TUnitConverterTypicalReturn } from '../types'

type TIrrigationPressureUnit = 'PSI' | 'kPa'

export const irrigationPressure: Record<
  'irrigationPressure',
  (
    pressure?: number | null,
    options?: TUnitConverterOptionsWithUnits<TIrrigationPressureUnit>,
  ) => TUnitConverterTypicalReturn
> = {
  irrigationPressure: (
    pressure: number | null = null,
    {
      decimalPlaces,
      inputUnit = 'PSI',
      outputUnit = userDetailsStore.getState().pressure === 'IMPERIAL' ? 'PSI' : 'kPa',
    } = {},
  ) => {
    const defaultNumberOfDecimalPlaces = 1
    const shouldConvertFromPSIToKPA = inputUnit === 'PSI' && outputUnit === 'kPa'
    const shouldConvertFromKPAToPSI = inputUnit === 'kPa' && outputUnit === 'PSI'

    let convertedValue: number | null

    if (isNil(pressure)) {
      convertedValue = null
    } else if (shouldConvertFromPSIToKPA) {
      convertedValue = pressure * 6.895
    } else if (shouldConvertFromKPAToPSI) {
      convertedValue = pressure / 6.895
    } else {
      convertedValue = pressure
    }

    const decimalPlacesToUse = decimalPlaces ?? defaultNumberOfDecimalPlaces

    const unitSymbol =
      outputUnit === 'PSI'
        ? translate.measurements.poundsPerSquareInch.unit()
        : translate.measurements.kilopascals.unit()

    const label = translate.phrases.banyanApp('Irrigation Pressure')

    return {
      categoryTitle: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      categoryTitleWithoutUnit: () => label,
      defaultNumberOfDecimalPlaces: () => defaultNumberOfDecimalPlaces,
      suffix: () => unitSymbol,
      shortTitle: () => label,
      title: () => translate.phrases.templates('{{label}} ({{unitSymbol}})', { label, unitSymbol }),
      titleWithoutUnit: () => label,
      value: () => {
        if (isNil(convertedValue)) return null

        return +convertedValue?.toFixed(decimalPlacesToUse)
      },
      valueAsString: () => {
        return outputUnit === 'PSI'
          ? translate.measurements.poundsPerSquareInch.value(convertedValue, decimalPlacesToUse)
          : translate.measurements.kilopascals.value(convertedValue, decimalPlacesToUse)
      },
      valueWithSuffix: () => {
        return outputUnit === 'PSI'
          ? translate.measurements.poundsPerSquareInch.valueWithUnit(convertedValue, decimalPlacesToUse)
          : translate.measurements.kilopascals.valueWithUnit(convertedValue, decimalPlacesToUse)
      },
      valueWithNoRounding: () => convertedValue,
    }
  },
}
